import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Grid,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const JournalHeader = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Drawer and submenu state
  const [topDrawerOpen, setTopDrawerOpen] = useState(false);
  const [bottomDrawerOpen, setBottomDrawerOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [guidelineOpen, setGuidelineOpen] = useState(false);

  // Hover menus for desktop
  const [aboutAnchorEl, setAboutAnchorEl] = useState(null);
  const aboutOpenMenu = Boolean(aboutAnchorEl);
  const handleAboutOpen = (e) => setAboutAnchorEl(e.currentTarget);
  const handleAboutClose = () => setAboutAnchorEl(null);

  const [issueAnchorEl, setIssueAnchorEl] = useState(null);
  const issueOpenMenu = Boolean(issueAnchorEl);
  const handleIssueOpen = (e) => setIssueAnchorEl(e.currentTarget);
  const handleIssueClose = () => setIssueAnchorEl(null);

  const [guidelinesAnchorEl, setGuidelinesAnchorEl] = useState(null);
  const guidelinesOpenMenu = Boolean(guidelinesAnchorEl);
  const handleGuidelinesOpen = (e) => setGuidelinesAnchorEl(e.currentTarget);
  const handleGuidelinesClose = () => setGuidelinesAnchorEl(null);

  return (
    <Box>
      {/* Top Navbar */}
      <AppBar position="static" sx={{ backgroundColor: '#3674B5' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Sabudhdhi Journals</Typography>

          {isMobile ? (
            <>
              <IconButton edge="end" color="inherit" onClick={() => setTopDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>

              <Drawer anchor="right" open={topDrawerOpen} onClose={() => setTopDrawerOpen(false)}>
                <List sx={{ width: 250 }}>
                  <ListItem button component={Link} to="/home">Home</ListItem>

                  {/* About Submenu */}
                  <ListItem button onClick={() => setAboutOpen(!aboutOpen)}>
                    <ListItemText primary="About" />
                    {aboutOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItem>
                  <Collapse in={aboutOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItem button component={Link} to="/about-journal" sx={{ pl: 4 }}>About the Journal</ListItem>
                      <ListItem button component={Link} to="/index" sx={{ pl: 4 }}>Index</ListItem>
                      <ListItem button component={Link} to="/editorial-board" sx={{ pl: 4 }}>Editorial Board</ListItem>
                      <ListItem button component={Link} to="/submission" sx={{ pl: 4 }}>Submission</ListItem>
                      <ListItem button component={Link} to="/contact-us" sx={{ pl: 4 }}>Contact Us</ListItem>
                    </List>
                  </Collapse>

                  <ListItem button component={Link} to="/register">Register</ListItem>
                  <ListItem button component={Link} to="/login">Login</ListItem>
                </List>
              </Drawer>
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Button component={Link} to="/home" sx={{ color: '#fff' }}>Home</Button>

              {/* About Desktop Menu */}
              <Box onMouseEnter={handleAboutOpen} onMouseLeave={handleAboutClose}>
                <Button
                  sx={{ color: '#fff' }}
                  endIcon={<ExpandMoreIcon />}
                >
                  About
                </Button>
                <Menu
                  anchorEl={aboutAnchorEl}
                  open={aboutOpenMenu}
                  onClose={handleAboutClose}
                  MenuListProps={{ onMouseLeave: handleAboutClose }}
                >
                  <MenuItem component={Link} to="/about-journal">About the Journal</MenuItem>
                  <MenuItem component={Link} to="/index">Index</MenuItem>
                  <MenuItem component={Link} to="/editorial-board">Editorial Board</MenuItem>
                  <MenuItem component={Link} to="/submission">Submission</MenuItem>
                  <MenuItem component={Link} to="/contact-us">Contact Us</MenuItem>
                </Menu>
              </Box>

              <Button component={Link} to="/register" sx={{ color: '#fff' }}>Register</Button>
              <Button component={Link} to="/login" sx={{ color: '#fff' }}>Login</Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Middle Section */}
      <Box sx={{ backgroundColor: '#F5F0CD', py: 2 }}>
        <Container>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Journal of Sabudhdhi Research
          </Typography>
        </Container>
      </Box>

      {/* Bottom Navbar */}
      <Box sx={{ backgroundColor: '#FADA7A', py: 1 }}>
        <Container>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              {isMobile ? (
                <>
                  <IconButton edge="start" sx={{ color: '#fff' }} onClick={() => setBottomDrawerOpen(true)}>
                    <MenuIcon />
                  </IconButton>

                  <Drawer anchor="right" open={bottomDrawerOpen} onClose={() => setBottomDrawerOpen(false)}>
                    <List sx={{ width: 250 }}>
                      {/* Issue Submenu */}
                      <ListItem button onClick={() => setIssueOpen(!issueOpen)}>
                        <ListItemText primary="Issue" />
                        {issueOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItem>
                      <Collapse in={issueOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <ListItem button component={Link} to="/" sx={{ pl: 4 }}>Current Issue</ListItem>
                          <ListItem button component={Link} to="/past-issues" sx={{ pl: 4 }}>Past Issues</ListItem>
                          <ListItem button component={Link} to="/special-issue" sx={{ pl: 4 }}>Special Issue</ListItem>
                        </List>
                      </Collapse>

                      <ListItem button component={Link} to="/archives">Archives</ListItem>
                      <ListItem button component={Link} to="/announcements">Announcements</ListItem>

                      {/* Guidelines Submenu */}
                      <ListItem button onClick={() => setGuidelineOpen(!guidelineOpen)}>
                        <ListItemText primary="Guidelines" />
                        {guidelineOpen ? <ExpandLess /> : <ExpandMore />}
                      </ListItem>
                      <Collapse in={guidelineOpen} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                          <ListItem button component={Link} to="/author-guidelines" sx={{ pl: 4 }}>Author Guidelines</ListItem>
                          <ListItem button component={Link} to="/submission-guidelines" sx={{ pl: 4 }}>Submission Guidelines</ListItem>
                          <ListItem button component={Link} to="/review-process" sx={{ pl: 4 }}>Peer Review Process</ListItem>
                        </List>
                      </Collapse>

                      <ListItem button component={Link} to="/article-fee">Article Fee</ListItem>
                      <ListItem button component={Link} to="/policy">Policy of Journal</ListItem>
                      {/* <ListItem button component={Link} to="/research">
                        <ListItemText primary="Research" />
                      </ListItem> */}
                    </List>
                  </Drawer>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box onMouseEnter={handleIssueOpen} onMouseLeave={handleIssueClose}>
                    <Button sx={{ color: '#578FCA' }} endIcon={<ExpandMoreIcon />}>Issue</Button>
                    <Menu
                      anchorEl={issueAnchorEl}
                      open={issueOpenMenu}
                      onClose={handleIssueClose}
                      MenuListProps={{ onMouseLeave: handleIssueClose }}
                    >
                      <MenuItem component={Link} to="/">Current Issue</MenuItem>
                      <MenuItem component={Link} to="/past-issues">Past Issues</MenuItem>
                      <MenuItem component={Link} to="/special-issue">Special Issue</MenuItem>
                    </Menu>
                  </Box>

                  <Button component={Link} to="/archives" sx={{ color: '#578FCA' }}>Archives</Button>
                  <Button component={Link} to="/announcements" sx={{ color: '#578FCA' }}>Announcements</Button>

                  <Box onMouseEnter={handleGuidelinesOpen} onMouseLeave={handleGuidelinesClose}>
                    <Button sx={{ color: '#578FCA' }} endIcon={<ExpandMoreIcon />}>Guidelines</Button>
                    <Menu
                      anchorEl={guidelinesAnchorEl}
                      open={guidelinesOpenMenu}
                      onClose={handleGuidelinesClose}
                      MenuListProps={{ onMouseLeave: handleGuidelinesClose }}
                    >
                      <MenuItem component={Link} to="/author-guidelines">Author Guidelines</MenuItem>
                      <MenuItem component={Link} to="/submission-guidelines">Submission Guidelines</MenuItem>
                      <MenuItem component={Link} to="/review-process">Peer Review Process</MenuItem>
                    </Menu>
                  </Box>

                  <Button component={Link} to="/article-fee" sx={{ color: '#578FCA' }}>Article Fee</Button>
                  <Button component={Link} to="/policy" sx={{ color: '#578FCA' }}>Policy of Journal</Button>
                  {/* <Button component={Link} to="/research" sx={{ color: '#578FCA' }}>Research</Button> */}
                </Box>
              )}
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#578FCA' }}>
                <SearchIcon sx={{ mr: 1 }} />
                <Typography>Search</Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default JournalHeader;
